import { z } from "zod";

import { PlaceOfSafetyTypeSchema } from "@thalamos/common";

import { IncidentFormIdSchema } from "./incidentWorkItem.js";

/**
 * Admin-only retrospective correction of the Place of Safety recorded on an
 * ENDED incident. Many incidents have a place of safety that was typed in by
 * hand (an "ad-hoc" entry with no curated `id`) when it should have been linked
 * to a curated place of safety - e.g. "Queens Hospital" instead of selecting the
 * curated "Queen's Hospital". This re-links such entries to the correct curated
 * record so analytics attribute them correctly, and can also correct the recorded
 * place-of-safety type.
 *
 * Each relink targets one `recordPlaceOfSafety` form by `formId` and may change the
 * curated place of safety (`placeOfSafetyId`), the place-of-safety type, or both.
 * The server resolves the authoritative name/address/postcode/ODS code from the
 * curated list, so the client cannot inject arbitrary place-of-safety data.
 */
export const AdminRelinkPlaceOfSafetyTypeSchema = z.object({
  value: PlaceOfSafetyTypeSchema,
  valueLabel: z.string().min(1),
});
export type AdminRelinkPlaceOfSafetyType = z.infer<
  typeof AdminRelinkPlaceOfSafetyTypeSchema
>;

export const AdminRelinkPlaceOfSafetyEditSchema = z
  .object({
    formId: IncidentFormIdSchema,
    placeOfSafetyId: z.string().min(1).optional(),
    placeOfSafetyType: AdminRelinkPlaceOfSafetyTypeSchema.optional(),
  })
  .refine(
    (r) => r.placeOfSafetyId !== undefined || r.placeOfSafetyType !== undefined,
    {
      message: "Each relink must change the place of safety, its type, or both",
    },
  );
export type AdminRelinkPlaceOfSafetyEdit = z.infer<
  typeof AdminRelinkPlaceOfSafetyEditSchema
>;

export const AdminRelinkPlaceOfSafetyRequestSchema = z
  .object({
    reason: z.string().min(1),
    ccmNumber: z.string().min(1),
    relinks: z.array(AdminRelinkPlaceOfSafetyEditSchema).min(1),
  })
  .refine(
    ({ relinks }) => {
      const seen = new Set(relinks.map((r) => r.formId));
      return seen.size === relinks.length;
    },
    { message: "Only one relink is allowed per form", path: ["relinks"] },
  );

export type AdminRelinkPlaceOfSafetyRequest = z.infer<
  typeof AdminRelinkPlaceOfSafetyRequestSchema
>;

/**
 * Plain (unbranded) shape for use as a TSOA `@Body()` type on the controller and
 * the frontend request payload. Kept structurally in sync with the zod schema above.
 */
export type AdminRelinkPlaceOfSafetyRequestUnbranded = {
  reason: string;
  ccmNumber: string;
  relinks: {
    formId: string;
    placeOfSafetyId?: string;
    placeOfSafetyType?: { value: string; valueLabel: string };
  }[];
};
